import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  isDevMode,
  numberAttribute,
  untracked,
  viewChild
} from '@angular/core';
import { OSKIconName } from '../../types/osk-theme.types';
import { OSKIconRegistry } from './osk-icon.registry';
import { OSKIconDrawing } from './osk-icon.types';

/**
 * Atom: <osk-icon>
 * SVG icon drawn from OSKIconRegistry. Built-in icons that are not registered yet are loaded
 * lazily on first use; register your own with `provideOskIcons()`.
 */
@Component({
  selector: 'osk-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
        flex-shrink: 0;
      }
      svg {
        display: block;
      }
    `
  ],
  template: `
    @if (drawing(); as icon) {
      <svg
        #svg
        [attr.width]="width()"
        [attr.height]="size()"
        [attr.viewBox]="icon.viewBox ?? '0 0 24 24'"
        fill="none"
        [attr.stroke]="icon.variant === 'fill' ? null : 'currentColor'"
        [attr.stroke-width]="icon.variant === 'fill' ? null : strokeWidth()"
        [attr.stroke-linecap]="icon.variant === 'fill' ? null : 'round'"
        [attr.stroke-linejoin]="icon.variant === 'fill' ? null : 'round'"
        aria-hidden="true"
      ></svg>
    }
  `
})
export class OSKIconComponent {
  private readonly registry = inject(OSKIconRegistry);
  private readonly svg = viewChild<ElementRef<SVGSVGElement>>('svg');

  readonly name = input.required<OSKIconName>();
  readonly size = input(16, { transform: numberAttribute });
  readonly strokeWidth = input(1.75, { transform: numberAttribute });

  /** Drawing for the current name and size; undefined until a lazily loaded icon arrives. */
  protected readonly drawing = computed<OSKIconDrawing | undefined>(() => {
    const icon = this.registry.get(this.name());
    return icon?.large && this.size() >= icon.large.from ? icon.large : icon;
  });

  protected readonly width = computed<number>(() => {
    const ratio = this.drawing()?.aspectRatio ?? 1;
    return ratio === 1 ? this.size() : Math.round(this.size() * ratio);
  });

  constructor() {
    // Writes the drawing into the <svg> directly. Icon markup only comes from registered definitions
    // (library code or the app's own), never user input; this also keeps @oskey/ui free of
    // @angular/platform-browser, which the apps do not path-map for the library.
    effect(() => {
      const svg = this.svg()?.nativeElement;
      if (svg) {
        svg.innerHTML = this.drawing()?.content ?? '';
      }
    });
    effect(() => {
      const name = this.name();
      if (this.registry.get(name)) {
        return;
      }
      untracked(() =>
        this.registry.loadBuiltIns().then(() => {
          if (isDevMode() && !this.registry.get(name)) {
            console.warn(`<osk-icon>: unknown icon "${name}". Register it with provideOskIcons().`);
          }
        })
      );
    });
  }
}
